import { config, logoutUri, redirectUri } from '../config'
import { challengeFor, createState, createVerifier } from './pkce'

// The credential, and how it is obtained.
//
// One rule holds across both modes: **the ID token is the credential**.
// The authoring surface authorises on the subject and the `admin` group
// claim, and neither rides on a Cognito access token. Sending the access
// token would fail at the authorizer with a message about neither.
//
// The access token is kept beside it for one reader only: the account
// port (docs/plan/account.md §4), which calls the pool's own API on the
// signed-in account. Nothing sends it to ours.

const STORAGE_TOKENS = 'bluu.session'
const STORAGE_PENDING = 'bluu.login'
const STORAGE_RETURN_TO = 'bluu.returnTo'

/**
 * Tokens live in `sessionStorage`, not `localStorage`.
 *
 * A credential that outlives the tab is a credential still sitting on
 * disk tomorrow, and the pool's hosted session cookie already makes a new
 * tab a redirect rather than a re-login — so persisting buys convenience
 * that is already there and costs exposure that is not.
 */
interface StoredTokens {
  idToken: string
  /**
   * For the pool's API, never ours. Optional because a tab signed in
   * before it was kept holds none until its next refresh.
   */
  accessToken?: string
  refreshToken?: string
  /** Epoch milliseconds. Read from the ID token's own `exp`. */
  expiresAt: number
}

interface PendingLogin {
  verifier: string
  state: string
}

/** Seconds of headroom: refresh before expiry rather than after a 401. */
const REFRESH_SKEW_SECONDS = 60

let tokens: StoredTokens | null = readTokens()
let refreshInFlight: Promise<string | null> | null = null

const listeners = new Set<() => void>()

function readTokens(): StoredTokens | null {
  const raw = sessionStorage.getItem(STORAGE_TOKENS)
  if (!raw) return null
  try {
    return JSON.parse(raw) as StoredTokens
  } catch {
    sessionStorage.removeItem(STORAGE_TOKENS)
    return null
  }
}

function writeTokens(next: StoredTokens | null): void {
  tokens = next
  if (next) sessionStorage.setItem(STORAGE_TOKENS, JSON.stringify(next))
  else sessionStorage.removeItem(STORAGE_TOKENS)
  for (const listener of listeners) listener()
}

/** Subscribe to sign-in and sign-out, for `useSyncExternalStore`. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * The claims the client itself reads.
 *
 * Read for display and for the tenant to open on — never for
 * authorisation, which is the server's and is a membership lookup rather
 * than anything on the token. Decoded without verifying the signature,
 * deliberately: a browser verifying a token it was just handed proves
 * nothing, and the API Gateway authorizer is what actually verifies it.
 */
export interface Claims {
  sub?: string
  email?: string
  /** What the reader calls themselves, set on the account screen. On the token through `profile`. */
  name?: string
  exp?: number
  /**
   * The tenant the client opens on — a default, not a permission. Since
   * tenancy became a membership this claim is no longer an authorisation
   * input; `GET /v1/authoring/tenants` is.
   */
  'custom:tenant'?: string
  /** The pool groups the identity is in. `operator` opens the operator area. */
  'cognito:groups'?: string[]
}

export function decodeClaims(idToken: string): Claims {
  const payload = idToken.split('.')[1]
  if (!payload) return {}
  const padded = payload.replace(/-/g, '+').replace(/_/g, '/')
  try {
    return JSON.parse(decodeURIComponent(escape(atob(padded)))) as Claims
  } catch {
    return {}
  }
}

export function currentClaims(): Claims | null {
  return tokens ? decodeClaims(tokens.idToken) : null
}

/**
 * Whether to draw the operator area.
 *
 * A courtesy to the reader, not a guard: the operator surface checks the
 * group itself and answers anyone else with a 403. The local loop runs
 * credential-free and its one caller is the operator.
 */
export function isOperator(): boolean {
  if (config.authMode === 'none') return true
  return (currentClaims()?.['cognito:groups'] ?? []).includes('operator')
}

export function isAuthenticated(): boolean {
  return config.authMode === 'none' || tokens !== null
}

function expiryOf(idToken: string): number {
  const { exp } = decodeClaims(idToken)
  // A token with no readable `exp` is treated as already stale rather
  // than as eternal: the failure of guessing long is a request that 401s
  // with no path back, and the failure of guessing short is one refresh.
  return exp ? exp * 1000 : 0
}

// ---------------------------------------------------------------------
// The hosted flow
// ---------------------------------------------------------------------

/**
 * Send the author to the pool's hosted UI.
 *
 * `returnTo` is where they land afterwards, so a deep link into a tenant
 * survives a login. It is a same-origin path and is stored rather than
 * round-tripped through `state`, which is the CSRF guard and is compared
 * for equality — putting a destination in it would make the guard carry
 * attacker-chosen content.
 */
export async function beginLogin(returnTo: string): Promise<void> {
  if (!config.cognito) throw new Error('beginLogin called with no Cognito configuration')

  const verifier = createVerifier()
  const state = createState()
  const pending: PendingLogin = { verifier, state }
  sessionStorage.setItem(STORAGE_PENDING, JSON.stringify(pending))
  sessionStorage.setItem(STORAGE_RETURN_TO, returnTo)

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.cognito.clientId,
    redirect_uri: redirectUri,
    scope: config.cognito.scopes,
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
  })
  window.location.assign(`${config.cognito.domain}/oauth2/authorize?${params}`)
}

/**
 * Exchange the authorization code for tokens.
 *
 * Returns where to go next. Throws on a `state` mismatch rather than
 * exchanging anyway — an authorization response the client did not
 * initiate is the case this check exists for.
 */
export async function completeLogin(code: string, state: string): Promise<string> {
  if (!config.cognito) throw new Error('completeLogin called with no Cognito configuration')

  const raw = sessionStorage.getItem(STORAGE_PENDING)
  sessionStorage.removeItem(STORAGE_PENDING)
  if (!raw) throw new Error('Sign-in did not finish. Please start again.')

  const pending = JSON.parse(raw) as PendingLogin
  if (pending.state !== state) throw new Error('Sign-in did not finish. Please start again.')

  const response = await fetch(`${config.cognito.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.cognito.clientId,
      code,
      redirect_uri: redirectUri,
      code_verifier: pending.verifier,
    }),
  })
  if (!response.ok) {
    throw new Error(`Sign-in could not be completed (${response.status}). ${await response.text()}`)
  }

  const body = (await response.json()) as TokenResponse
  writeTokens({
    idToken: body.id_token,
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    expiresAt: expiryOf(body.id_token),
  })

  const returnTo = sessionStorage.getItem(STORAGE_RETURN_TO) ?? '/'
  sessionStorage.removeItem(STORAGE_RETURN_TO)
  return returnTo
}

/** What the token endpoint answers, for both grants the client uses. */
interface TokenResponse {
  id_token: string
  access_token: string
  refresh_token?: string
}

async function refresh(): Promise<string | null> {
  if (!config.cognito || !tokens?.refreshToken) return null

  const response = await fetch(`${config.cognito.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.cognito.clientId,
      refresh_token: tokens.refreshToken,
    }),
  })
  if (!response.ok) {
    // A refresh token the pool will not honour means the session is over
    // — revoked, expired, or the user deactivated. Clearing is what makes
    // the next render show the sign-in page instead of looping on 401s.
    writeTokens(null)
    return null
  }

  const body = (await response.json()) as TokenResponse
  writeTokens({
    idToken: body.id_token,
    // Issued together with the ID token, so replaced together with it.
    accessToken: body.access_token,
    // Cognito does not reissue the refresh token on this grant; keeping
    // the existing one is what makes the 30-day window mean 30 days.
    refreshToken: body.refresh_token ?? tokens.refreshToken,
    expiresAt: expiryOf(body.id_token),
  })
  return body.id_token
}

/**
 * The bearer token for the next request, refreshing first if it is about
 * to expire.
 *
 * `null` in `none` mode, which is the honest answer: the local loop has
 * no credential to send, so no `Authorization` header is set at all.
 * Concurrent callers share one refresh — every query on a screen would
 * otherwise start its own, and Cognito would serve one and reject the
 * rest.
 */
export async function bearerToken(): Promise<string | null> {
  return (await currentTokens())?.idToken ?? null
}

/**
 * The access token, for the pool's own API, refreshing first as
 * `bearerToken` does.
 *
 * Read only by the account port. It shares the one refresh in flight
 * with `bearerToken` — the two tokens are issued together, so they are
 * one refresh, not two. `null` in `none` mode and when signed out.
 */
export async function accessToken(): Promise<string | null> {
  return (await currentTokens())?.accessToken ?? null
}

/**
 * The stored tokens, refreshed first if they are about to expire. The
 * expiry is the ID token's; the access token is issued with it and lives
 * as long.
 */
async function currentTokens(): Promise<StoredTokens | null> {
  if (config.authMode === 'none') return null
  if (!tokens) return null
  if (Date.now() < tokens.expiresAt - REFRESH_SKEW_SECONDS * 1000) return tokens

  refreshInFlight ??= refresh().finally(() => {
    refreshInFlight = null
  })
  await refreshInFlight
  return tokens
}

/**
 * Drop the local session, without leaving the app.
 *
 * This is the involuntary half of signing out: the credential stopped
 * being honoured, so the tokens are discarded and the subscribers put the
 * sign-in screen back. It deliberately does *not* visit the pool's
 * logout — an author whose token merely expired still has a valid hosted
 * session, and sending them through logout would make the next sign-in a
 * password prompt instead of a redirect.
 */
export function endSession(): void {
  writeTokens(null)
  sessionStorage.removeItem(STORAGE_PENDING)
  sessionStorage.removeItem(STORAGE_RETURN_TO)
}

/** Drop the local session and send the author to the pool's logout. */
export function logout(): void {
  endSession()
  if (config.cognito) {
    const params = new URLSearchParams({
      client_id: config.cognito.clientId,
      logout_uri: logoutUri,
    })
    window.location.assign(`${config.cognito.domain}/logout?${params}`)
  }
}
